import _ from 'lodash';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import GlobalNotification from '../../../component/common/snackbar';
import i18n from '../../../i18n';
import { CurrentGroupAllRestaurantWarrants, Warrant, WarrantCode } from '../../../models';
import { LOCAL_STORAGE_KEYS } from '../../../modules/cache/local-storage.constant';
import LSM from '../../../modules/cache/tool-local-storage';
import apiManage from '../../../request';
import { groupUnitData } from '../group-unit';
import { ConditionType } from '../../../component/form/search-key-input.component';

class AuthorizationData {

    constructor() {
        makeAutoObservable(this);
    }

    // ****************
    // Attribute
    // ****************
    // 组件类型 - 集团 | 餐厅
    comType: 'GROUP' | 'RESTAURANT' = 'GROUP';
    // 加载
    isLoading: boolean = false;
    // 集团stringId
    belongedGroup: string = '';
    // 集团名称
    belongedGroupName: string = '';
    // 餐厅stringId
    restaurantStringId: string = '';
    // 搜索key
    searchKey: string = 'name';
    // 搜索key 2
    searchSecondKey: string = 'DATE';
    // 1 升序 -1 降序
    searchSort: string = '1';
    // 搜索value
    searchValue: string = '';
    // 批量删除
    isPatchFlag: boolean = false;
    // 视图模式
    viewMode: 'DETAIL' | 'SUMMARY' = 'DETAIL';
    // 筛选激活与过期授权的下拉选择值
    activeType: 'USE' | 'EXPIRED' | 'INACTIVATED' = 'USE';
    // 集团授权
    groupWarrantList: Warrant[] = [];
    // 餐厅授权
    restuarantWarrantList: CurrentGroupAllRestaurantWarrants[] = [];
    // 批量选择 - 待删除的
    selectedList: AuthDeleteList[] = [];
    // 确认弹窗
    confirmDialogData: {
        visible: boolean;
        title: string;
        content: string;
        handleClick?: () => void;
        onCancel?: () => void;
    } = {
            visible: false,
            title: '',
            content: '',
        };
    // 选择权限点弹窗
    authPointDialogData: {
        open: boolean;
        onCancel: () => void;
        operations: { stringId: string, target: string };
    } = {
            open: false,
            onCancel: () => { },
            operations: { stringId: '', target: 'RESTAURANT' }
        }
    // 选择权限点详情弹窗
    authPointInfoDialogData: {
        open: boolean;
        onCancel: () => void;
        operations: { stringId: string, target: string };
        type: 'ADD' | 'DELETE' | 'ACTIVE';
        selectAuth?: Warrant;
    } = {
            open: false,
            onCancel: () => { },
            type: 'ADD',
            operations: { stringId: '', target: 'RESTAURANT' }
        }
    // 搜索条件
    conditions = observable<ConditionType>([]);

    // 设置搜索条件
    setConditions(val: ConditionType[]) {
        this.conditions.replace(val);
        this.getAuthData();
    }

    // ****************
    // Function
    // ****************

    async init(comType: 'GROUP' | 'RESTAURANT', restaurantStringId: string = this.restaurantStringId) {
        runInAction(() => {
            this.comType = comType;
            this.belongedGroup = groupUnitData?.belongedGroup;
            this.belongedGroupName = groupUnitData?.belongedGroupName;
            this.restaurantStringId = restaurantStringId || '';
            this.isPatchFlag = false;
            this.selectedList = [];
            this.restuarantWarrantList = [{
                warrants: []
            }];
        })
        await this.getAuthData();
    }


    // 获取 集团下的授权点
    async getAuthData() {
        this.isLoading = true;

        const language = i18n.language === 'CN' ? 'CN' : 'EN';

        const extraParams = {};
        this.conditions.forEach((item) => {
            extraParams[item.key] = extraParams[item.key] ? _.concat(extraParams[item.key], item.value) : [item.value];
        })

        let params: any = {};
        params = {
            string_id: this.comType === 'GROUP' ? this.belongedGroup : this.restaurantStringId,
            sort_key: this.searchSecondKey,
            sort_value: this.searchSort,
            key: language,
            mode: this.viewMode,
            ...extraParams,
            type: this.activeType
        }

        if (this.comType === 'GROUP') {
            const result = await apiManage.get_group_warrant_condition(params);
            if (!result?.error) {
                let restuarantWarrantList = result?.resWarrantResult || [];
                // 搜索有key ：用于筛选条件后，过滤无授权的餐厅
                if (this.conditions?.length) {
                    restuarantWarrantList = restuarantWarrantList.filter((item) => {
                        return item?.warrants?.length
                    })
                }
                runInAction(() => {
                    this.groupWarrantList = result?.groupNewWarrants || [];
                    this.restuarantWarrantList = restuarantWarrantList || [];
                })
            } else {
                GlobalNotification.error(i18n.t('warrant_page_msg_failed_obtain_authorization_data'));

            }
        } else if (this.comType === 'RESTAURANT') {
            const result = await apiManage.get_warrant_condition(params);
            if (!result?.error) {
                runInAction(() => {
                    this.restuarantWarrantList = [{
                        string_id: this.restaurantStringId,
                        warrants: (result || []) as Warrant[]
                    }];
                })
            } else {
                GlobalNotification.error(i18n.t('warrant_page_msg_failed_obtain_authorization_data'));

            }
        }
        runInAction(() => {
            this.isLoading = false;
        })

    }

    updateGroupWarrantList(list: Warrant[]) {
        this.groupWarrantList = list;
    }
    async updateSearchKey(val: string) {
        this.searchKey = val;
    }
    async updateSearchSecondKey(val: string) {
        this.searchSecondKey = val;
        await this.getAuthData();

    }
    async updateIsPatchFlag() {
        this.isPatchFlag = !this.isPatchFlag;
        this.selectedList = [];
    }
    async updateSearchSort(val: string) {
        this.searchSort = val;
        await this.getAuthData();

    }
    async updateViewMode(v: 'DETAIL' | 'SUMMARY') {
        this.viewMode = v;
        await this.getAuthData();

    }
    async updateActiveType(val: typeof this.activeType) {
        this.activeType = val;
        await this.getAuthData();
    }
    updateSearchValue(val: string) {
        this.searchValue = val;
    }
    /**
     * 确认弹窗
     */
    updateConfirmDialog(params: {
        visible: boolean;
        title?: string;
        content?: string;
        handleClick?: () => void;
        onCancel?: () => void;
    }) {
        this.confirmDialogData = {
            onCancel: () => this.updateConfirmDialog({ ...this.confirmDialogData, visible: false }),
            visible: params.visible,
            title: params.title || '',
            content: params.content || '',
            handleClick: params.handleClick,
        };
    }
    /**
     * 选中复选框
     * - 1.存在去掉，不存在加上
     * - 2.汇总模式是数组形式，需要区分处理
     * - 3.如果不是批量操作，则是跳转到详情， 保存对应数据
     * - 4.@param operations 批量操作 - 删除操作 区分是删除餐厅还是删除集团授权点
     */
    toggleCheck(data: Warrant, operations: { stringId: string, target: string }): void {

        if (this.isPatchFlag) {
            let originSelectedList = _.cloneDeep(this.selectedList);
            if (this.viewMode === 'DETAIL') {
                // 查找删除列表中对应的授权点id
                const findListIndex = originSelectedList.findIndex(item => {
                    const findId = item.warrant_id.find(i => i === data._id);
                    return findId === data._id && item.acount_id === operations.stringId;
                });

                const warrantIdArr: string[] = [];
                if (findListIndex === -1) {
                    /**
                     * 1. @param acount_id 为对应操作餐厅或集团string_id  @typedef string
                     * 2. @param acount_type 表示区分选中的是餐厅或是集团下的授权点 @typedef string
                     * 3. @param warrant_id 表示选中操作的授权点的id @typedef string[]
                     */
                    warrantIdArr.push(data._id ?? '');
                    originSelectedList.push({ acount_id: operations.stringId, acount_type: operations.target, warrant_id: warrantIdArr });
                } else {
                    originSelectedList.splice(findListIndex, 1);
                }
            } else if (this.viewMode === 'SUMMARY') {
                const selectFlag = this.checkSelectStatus(data.warrants || []);
                const ids = data.warrants?.map(item => item._id!) || [];
                const copyIds = new Set(ids);
                if (selectFlag) {
                    // 存在 去重
                    originSelectedList = originSelectedList.filter(i => {
                        return i.warrant_id.every(j => !copyIds.has(j))
                    })
                } else {
                    /**
                     * 1. @param acount_id 为对应操作餐厅或集团string_id  @typedef string
                     * 2. @param acount_type 表示区分选中的是餐厅或是集团下的授权点 @typedef string
                     * 3. @param warrant_id 表示选中操作的授权点的id @typedef string[]
                     */
                    // 没有选中则推入
                    originSelectedList.push({ acount_id: operations.stringId, acount_type: operations.target, warrant_id: ids });
                }
            }
            this.selectedList = originSelectedList;

        }
        // 打开详情弹窗
        else {
            this.toggleAuthDialog(false, operations);
            this.toggleAuthPointInfoDialog(true, 'DELETE', data as Warrant);

            // this.setState({
            //     showDetailDialog: true,
            //     detailDialogMode: 'VIEW',
            //     currentWarrant: data instanceof Array ? data : [data],
            //     currentI18n: warranti18N ? warranti18N : (data as Warrant).warranti18N!,
            //     operationObject: operations
            // })
        }
    }
    // 判断是否勾选状态
    checkSelectStatus(warrants: Warrant[]): boolean {
        /**
         * 1.判断是否被选中
         */
        const deleteList = _.cloneDeep(this.selectedList);
        const ids = warrants.map(item => item._id);
        const copyIds = new Set(ids);
        const result = deleteList.filter(i => {
            return i.warrant_id.every(j => copyIds.has(j))
        });
        return result.length > 0
    }

    /**
     * 批量删除授权数据
     * -
     */
    async deleteWarrantData() {
        this.confirmDialogData?.onCancel?.();

        // 获取所删除的集团下的授权点数组 和 所删除的餐厅下的授权点数组
        const { groupDeleteIds, resDeleteArr } = this.getDeleteWarrantData();

        /**
         * 这里进行判断 如果删除的数据里面只有集团或餐厅中的其中一种
         * 则删除集团或只删除餐厅则不请求相应接口 返回一个ture
         * 因为考虑到用户可能只选择勾选集团下的授权点或者只勾选餐厅下的授权点进行删除
         * @param groupDeleteIds 要删除的集团warrant_id数组  @param resDeleteArr 要删除的餐厅warrant_id数组
         * @function deleteGroupWarrants() 删除集团请求的接口函数
         * @function deleteRestaurantWarrants() 删除餐厅请求的接口函数
         */
        await Promise.all([
            groupDeleteIds.length > 0 ? this.deleteGroupWarrants(groupDeleteIds) : Promise.resolve(true),
            resDeleteArr.length > 0 ? this.deleteRestaurantWarrants(resDeleteArr) : Promise.resolve(true)
        ]).then((values) => {
            let errorFlag: boolean = false;
            for (const res of values) {
                if (!res) {
                    errorFlag = true;
                    break;
                } else {
                    continue
                }
            }
            // 操作失败
            if (errorFlag) {
                GlobalNotification.error(i18n.t('warrant_page_msg_failed_delete_authorization_data'));
            }
            // 操作成功
            else {
                GlobalNotification.success(i18n.t('warrant_page_text_delete_authorization_data_successfully'));
                this.init(this.comType)
            }
        });
    }

    /**
     * 获取要删除的全部授权  集团 & 餐厅
     * @returns 返回删除的授权warrant_id字符串数组
     * @returns @param groupDeleteIds @typedef string[] 集团授权数组集合
     * @returns @param resDeleteArr @typedef Array<{stringId: string; deleteIds: string[];}>  餐厅授权数组集合
     */
    getDeleteWarrantData(): {
        // 集团要删除的数组
        groupDeleteIds: string[];
        // 餐厅要删除的数组
        resDeleteArr: Array<{
            stringId: string;
            deleteIds: string[];
        }>;
    } {
        const deleteItemList = _.cloneDeep(this.selectedList);
        // 集团要删除的数组  集团只有字符串数组
        const groupDeleteList: AuthDeleteList[] = [];
        // 餐厅要删除的数组
        const restaurantDeleteList: AuthDeleteList[] = [];
        // 集团下所有要删除的授权点warrant_id
        const groupDeleteIds: string[] = [];
        // 餐厅下所有要删除 授权点warrant_id  餐厅下可能有不同餐厅  用stringId区分不同餐厅
        let resDeleteArr: Array<{
            stringId: string;
            deleteIds: string[];
        }> = [];
        for (const item of deleteItemList) {
            // acount_type标识区分勾选里面要删除数据是集团授权数据还是餐厅授权数据
            item.acount_type === 'GROUP' ? groupDeleteList.push(item) : restaurantDeleteList.push(item)
        }
        // 只有集团的授权
        groupDeleteList.forEach(ele => {
            ele.warrant_id.forEach(id => {
                groupDeleteIds.push(id);
            })
        });
        // 只有餐厅的授权
        restaurantDeleteList.forEach(ele => {
            ele.warrant_id.forEach(id => {
                resDeleteArr.push({ stringId: ele.acount_id, deleteIds: [id] });
            })
        });
        // 合并同类项目用到的两个数据
        // 合并相同餐厅所用对象
        const correctResDeleteMap: {
            [key: string]: {
                stringId: string;
                deleteIds: string[];
            }
        } = {};
        // 返回正确数据  同一餐厅下的授权点数据
        const correctIdsArr: Array<{
            stringId: string;
            deleteIds: string[];
        }> = [];
        // 餐厅删除数组的特殊操作  合并同类项操作  合并同一个餐厅下的不同授权点
        for (const j in resDeleteArr) {
            if (resDeleteArr[j]) {
                const arr = resDeleteArr[j];
                // 不存在则加入
                if (!correctResDeleteMap[arr.stringId]) {
                    correctIdsArr.push({ stringId: arr.stringId, deleteIds: arr.deleteIds });
                    correctResDeleteMap[arr.stringId] = arr;
                } else {
                    for (const i in correctIdsArr) {
                        if (correctIdsArr[i]) {
                            const newArr = correctIdsArr[i];
                            // 存在相同餐厅ID则合并
                            if (newArr.stringId === arr.stringId) {
                                newArr.deleteIds = newArr.deleteIds.concat(arr.deleteIds);
                                break;
                            }
                        }
                    }
                }
                continue
            }
        }
        // 赋值
        resDeleteArr = correctIdsArr;
        return {
            groupDeleteIds,
            resDeleteArr
        }
    }

    // 删除集团授权
    async deleteGroupWarrants(deleteIds: string[]): Promise<boolean> {
        // 成功标识
        let successFlag: boolean = false;
        const account = LSM.get(LOCAL_STORAGE_KEYS.ACCOUNT) ?? {};
        const groupParams = {
            string_id: this.belongedGroup,
            ids: deleteIds,
            // 进行删除操作时传入操作员名称和id  记录到敏感操作记录里
            admin_id: account._id,
            admin_nickname: account.nickname
        }
        const result = await apiManage.delete_group_warrant_ids(groupParams);
        successFlag = !result?.error;
        return successFlag;
    }

    // 删除餐厅授权  接受参数resDeleteArr  要删除的餐厅授权数据
    async deleteRestaurantWarrants(resDeleteArr: Array<{ stringId: string; deleteIds: string[]; }>): Promise<boolean> {
        // 成功标识
        let successFlag: boolean = false;
        const account = LSM.get(LOCAL_STORAGE_KEYS.ACCOUNT) ?? {};
        for (const item of resDeleteArr) {
            // 参数
            const params = {
                string_id: item.stringId,
                ids: item.deleteIds,
                // 进行删除操作时传入操作员名称和id  记录到敏感操作记录里
                admin_id: account._id,
                admin_nickname: account.nickname
            }
            const result = await apiManage.delete_warrant_ids(params);

            successFlag = !result?.error;
        }
        return successFlag;
    }

    // 关闭/打开授权点弹窗
    toggleAuthDialog(open: boolean, operations?: { stringId: string, target: string }) {
        this.authPointDialogData = {
            open,
            onCancel: () => { this.toggleAuthDialog(false) },
            operations: operations!
        }
    }

    // 关闭/打开授权点详情弹窗
    toggleAuthPointInfoDialog(open: boolean, type?: 'ADD' | 'DELETE' | 'ACTIVE', selectAuth?: Warrant) {
        this.authPointInfoDialogData = {
            open,
            onCancel: () => { this.toggleAuthPointInfoDialog(false) },
            operations: this.authPointDialogData.operations,
            selectAuth,
            type: type || 'ADD'
        }
    }
    // 关闭/打开授权点续约弹窗
    async toggleRenewalDialog(operations?: { stringId: string, target: string, actionType: 'RENEWAL' },) {
        this.authPointDialogData = {
            open: false,
            onCancel: () => { this.toggleAuthDialog(false) },
            operations: operations!
        }
        const res = await apiManage.getRestaurantWarrants({ string_id: operations?.stringId || '', type: this.activeType as 'USE' | 'EXPIRED' });
        if (!res?.error) {
            if (!res.warrants?.length) {
                GlobalNotification.error(i18n.t('warrant_page_text_there_are_no_renewable_authorization_points'))
                return
            }
            const newWarrants = res.warrants.map((item) => {
                if (item?.total_num !== undefined || item.warrants?.[0]?.limit_point?.total_num !== undefined) {
                    item.limit_point = { total_num: item?.total_num ?? item.warrants?.[0]?.limit_point?.total_num };
                }
                delete item.warrants;
                return item
            })

            const selectAuth: Warrant = {
                code: '' as WarrantCode,
                warranti18N: {
                    name: [
                        { key: 'CN', value: i18n.t('warrant_page_text_renewal_of_contract', { lng: 'CN' }) },
                        { key: 'EN', value: i18n.t('warrant_page_text_renewal_of_contract', { lng: 'EN' }) }
                    ],
                    code: '' as WarrantCode,
                },
                warrants: newWarrants,
            }
            this.toggleAuthPointInfoDialog(true, 'ADD', selectAuth);

        }
    }

    // 关闭/打开激活弹窗
    async toggleActiveDialog(operations: { stringId: string, target: string }) {
        this.authPointDialogData = {
            open: false,
            onCancel: () => { this.toggleAuthDialog(false) },
            operations: operations!
        }
        const language = i18n.language === 'CN' ? 'CN' : 'EN';
        const params = {
            string_id: operations.stringId,
            sort_key: this.searchSecondKey,
            sort_value: this.searchSort,
            key: language,
            mode: 'DETAIL',
            type: 'INACTIVATED'
        }
        // 查询未激活的授权
        const result = await apiManage.get_warrant_condition(params);
        let warrants: Warrant[] = [];
        if (result && !result?.error) {
            warrants = result.reduce(
                (pre: Warrant[], cur: any) => pre.concat(cur?.warrants || []),
                [] as Warrant[]
            )
        }
        if (!warrants.length) {
            GlobalNotification.error(i18n.t('general_text_no_data'))
            return
        }

        const selectAuth = {
            warranti18N: {
                name: [{
                    key: 'CN', value: '激活'
                }, {
                    key: 'EN', value: 'Activate'
                }]
            },
            warrants
        }
        this.toggleAuthPointInfoDialog(true, 'ACTIVE', selectAuth);
    }
}

export default AuthorizationData;

//
/**
 * 待删除的授权数组
 * @param acount_id 为对应操作餐厅或集团string_id
 */
export type AuthDeleteList = {
    /**
     * 1. @param acount_id 为对应操作餐厅或集团string_id  @typedef string
     * 2. @param acount_type 表示区分选中的是餐厅或是集团下的授权点 @typedef string
     * 3. @param warrant_id 表示选中操作的授权点的id @typedef string[]
     */
    acount_id: string;
    acount_type: string;
    warrant_id: string[];
}